import React from 'react'
import { useParams } from 'react-router-dom'
import { WpsSectionsProvider } from '../../contexts/WpsSectionsContext'
import { useProjects } from '../../contexts/ProjectsContext'
import { useData360ProjectPipeSets } from '../../hooks/useData360ProjectPipeSets'
import { DATA_360_DISPLAY_NAME } from '../../utils/data360DisplayName'
import { usePhantomProjectRedirect } from '../../hooks/usePhantomProjectRedirect'
import PageLoader from '../ui/PageLoader'
import PipeSetSectionsOutlet from './PipeSetSectionsOutlet'

/**
 * Layout wrapper for all routes under /projects/:id/next/:pipeId/*.
 * Provides the WpsSectionsContext so all child routes share a single
 * sections fetch and can read/write via useWpsSections().
 */
export default function PipeSetLayout() {
  const { id, pipeId } = useParams()
  const { projectsLoading, getProject } = useProjects()
  const resolvingPhantom = usePhantomProjectRedirect()
  const project = id ? getProject(id) : undefined
  const { pipeSets, pipesLoading } = useData360ProjectPipeSets(project)

  if (!id || !pipeId) {
    return <p className="text-sm text-gray-500">Missing project or pipe set ID.</p>
  }

  if (resolvingPhantom || projectsLoading) {
    return <PageLoader label="Loading project…" />
  }

  if (!project) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
        <p className="font-medium text-gray-900">Project not found</p>
        <p className="mt-1">This project may have been removed or you may not have access.</p>
      </div>
    )
  }

  const pipeSet = pipeSets.find((pipe) => pipe.id === pipeId)
  if (!pipesLoading && !pipeSet) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
        <p className="font-medium text-gray-900">Pipe set not found</p>
        <p className="mt-1">Return to pipe sets and try again.</p>
      </div>
    )
  }

  if (pipesLoading) {
    return <PageLoader label={`Syncing pipes from ${DATA_360_DISPLAY_NAME}…`} />
  }

  return (
    <WpsSectionsProvider projectId={id} pipeSetId={pipeId}>
      <PipeSetSectionsOutlet />
    </WpsSectionsProvider>
  )
}
